import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, Info } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { submitLead, optimizeImg } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { track, trackLead } from "../lib/analytics";
import { ChipGroup } from "../components/ChipGroup";
import { overline, inputClass, labelClass, StepHeading, BriefRow } from "../components/FlowBits";

const HERO = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=85";
const labels = (list, ids) => list.filter((o) => ids.includes(o.id)).map((o) => o.label);

export default function OwnerFlow() {
  const { t, lang } = useLang();
  const o = (k) => t(`ownerPage.${k}`);
  const usageOpts = o("usageOptions");
  const areas = o("areas");
  const bedroomOpts = o("bedrooms");
  const goals = o("goals");
  const L = o("labels");

  const [usage, setUsage] = useState("");
  const [usageYear, setUsageYear] = useState("");
  const [area, setArea] = useState("");
  const [size, setSize] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [goalIds, setGoalIds] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const usageLabel = labels(usageOpts, [usage])[0] || "";
  const areaLabel = labels(areas, [area])[0] || "";
  const bedroomLabel = labels(bedroomOpts, [bedrooms])[0] || "";
  const goalLabels = labels(goals, goalIds);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const started = useRef(false);
  useEffect(() => { if (!started.current) { started.current = true; track("owner_estimate_start", { lead_type: "owner", cta_location: "owner_funnel" }); } }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!usage) { toast.error(o("usageRequired")); scrollTo("owner-step-usage"); return; }
    if (!area) { toast.error(o("areaRequired")); scrollTo("owner-step-area"); return; }
    setLoading(true);
    const message = [
      "OWNER BRIEF",
      `Changement d'usage: ${usageLabel}${usage === "yes" && usageYear ? ` (${usageYear})` : ""}`,
      `Neighbourhood: ${areaLabel}`,
      `Size: ${size ? `${size} m²` : "-"}`,
      `Bedrooms: ${bedroomLabel || "-"}`,
      `Goals: ${goalLabels.join("; ") || "-"}`,
      form.note.trim() ? `Note: ${form.note.trim()}` : null,
    ].filter(Boolean).join("\n");
    try {
      await submitLead({
        name: form.name, email: form.email, phone: form.phone, interest: "manage", message, source: "owner-brief", lang,
        meta: { usage, usage_year: usageYear || null, area, size_m2: size || null, bedrooms, goals: goalIds },
      });
      track("owner_estimate_complete", { lead_type: "owner", cta_location: "owner_funnel", goals_count: goalIds.length });
      trackLead("owner", "owner_funnel");
      navigate(`/${lang}/owner/thank-you`, { state: { name: form.name } });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };


  const propertyLine = [size ? `${size} m²` : null, bedroomLabel || null].filter(Boolean).join(" · ");

  return (
    <div data-testid="owner-page">
      <Seo title={o("seoTitle")} description={o("seoDesc")} />

      <section className="relative w-full min-h-[60vh] flex items-end vl-grain overflow-hidden">
        <img src={optimizeImg(HERO, 1920)} alt="Managed apartment in Nice" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/30" />
        <div className="relative px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] w-full pt-40 pb-16 md:pb-24">
          <Reveal>
            <p className={overline}>{o("overline")}</p>
            <h1 className="mt-5 font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-3xl whitespace-pre-line">{o("title")}</h1>
            <p className="mt-8 font-body font-light text-lg text-foreground/80 max-w-2xl leading-relaxed">{o("intro")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <form onSubmit={handleSubmit} data-testid="owner-form" className="lg:col-span-7 space-y-20">
            <Reveal>
              <div id="owner-step-usage" data-testid="owner-step-usage" className="scroll-mt-32">
                <StepHeading n={1} title={o("usageTitle")} hint={o("usageHint")} />
                <div className="mb-6 flex items-start gap-3 border border-primary/25 bg-primary/5 p-4 font-body text-sm text-foreground/75 leading-relaxed">
                  <Info size={16} className="text-primary shrink-0 mt-0.5" />
                  <span data-testid="owner-usage-explainer">{o("usageExplainer")}</span>
                </div>
                <ChipGroup options={usageOpts} value={usage} onChange={setUsage} testPrefix="usage" label={o("usageTitle")} />
                {usage === "yes" ? (
                  <div className="mt-6 max-w-xs flex flex-col gap-2">
                    <label htmlFor="owner-usage-year" className={labelClass}>{o("usageYear")}</label>
                    <input id="owner-usage-year" data-testid="owner-usage-year" type="number" inputMode="numeric" min="2000" max="2030" placeholder="2024" value={usageYear} onChange={(e) => setUsageYear(e.target.value)} className={inputClass} />
                  </div>
                ) : null}
              </div>
            </Reveal>

            <Reveal>
              <div id="owner-step-area" data-testid="owner-step-area" className="scroll-mt-32">
                <StepHeading n={2} title={o("areaTitle")} hint={o("areaHint")} />
                <ChipGroup options={areas} value={area} onChange={setArea} testPrefix="area" label={o("areaTitle")} />
              </div>
            </Reveal>

            <Reveal>
              <div data-testid="owner-step-property">
                <StepHeading n={3} title={o("propertyTitle")} hint={o("propertyHint")} />
                <div className="max-w-xs flex flex-col gap-2 mb-8">
                  <label htmlFor="owner-size" className={labelClass}>{o("sizeLabel")}</label>
                  <div className="flex items-end gap-3">
                    <input id="owner-size" data-testid="owner-size" type="number" inputMode="numeric" min="10" max="2000" placeholder="65" value={size} onChange={(e) => setSize(e.target.value)} className={inputClass} />
                    <span className="font-body text-sm text-foreground/60 pb-3">m²</span>
                  </div>
                </div>
                <p className={`${labelClass} mb-3`}>{o("bedroomsLabel")}</p>
                <ChipGroup options={bedroomOpts} value={bedrooms} onChange={setBedrooms} testPrefix="bedrooms" label={o("bedroomsLabel")} />
              </div>
            </Reveal>

            <Reveal>
              <div data-testid="owner-step-goals">
                <StepHeading n={4} title={o("goalsTitle")} hint={o("goalsHint")} />
                <ChipGroup options={goals} value={goalIds} onChange={setGoalIds} multi testPrefix="goal" label={o("goalsTitle")} />
              </div>
            </Reveal>

            <Reveal>
              <div data-testid="owner-step-details">
                <StepHeading n={5} title={o("formTitle")} hint={o("formIntro")} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="owner-name" className={labelClass}>{t("contact.name")}</label>
                    <input id="owner-name" data-testid="owner-name" name="name" autoComplete="name" required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="owner-email" className={labelClass}>{t("contact.email")}</label>
                    <input id="owner-email" data-testid="owner-email" name="email" type="email" autoComplete="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-2 sm:col-span-2">
                    <label htmlFor="owner-phone" className={labelClass}>{t("contact.phone")}</label>
                    <input id="owner-phone" data-testid="owner-phone" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-2 sm:col-span-2">
                    <label htmlFor="owner-note" className={labelClass}>{o("note")}</label>
                    <textarea id="owner-note" data-testid="owner-note" name="note" rows={4} value={form.note} onChange={(e) => update("note", e.target.value)} placeholder={o("notePlaceholder")} className={`${inputClass} resize-none`} />
                    <p className="font-body text-xs text-muted-foreground/80 leading-relaxed">{o("noteHelp")}</p>
                  </div>
                </div>
                <button type="submit" data-testid="owner-submit" disabled={loading} className="mt-10 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
                  {loading ? <><Loader2 size={15} className="animate-spin" /> {t("contact.sending")}</> : <>{o("submit")} <ArrowRight size={15} /></>}
                </button>
                <p className="mt-4 font-body text-xs text-muted-foreground/70">{o("privacy")}</p>
              </div>
            </Reveal>
          </form>

          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <Reveal delay={0.1}>
                <div data-testid="owner-brief" className="border border-primary/30 bg-secondary p-8">
                  <p className={overline}>{o("summaryTitle")}</p>
                  <dl className="mt-6 space-y-4">
                    <BriefRow label={L.usage} value={usageLabel ? `${usageLabel}${usage === "yes" && usageYear ? ` (${usageYear})` : ""}` : L.none} testId="brief-usage" />
                    <BriefRow label={L.area} value={areaLabel || L.none} testId="brief-area" />
                    <BriefRow label={L.property} value={propertyLine || L.none} testId="brief-property" />
                    <BriefRow label={L.goals} value={goalLabels.join(" · ") || L.none} testId="brief-goals" />
                  </dl>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <ul className="space-y-3">
                  {o("trust").map((line) => (
                    <li key={line} className="flex items-start gap-3 font-body font-light text-sm text-foreground/70">
                      <ShieldCheck size={15} className="text-primary shrink-0 mt-0.5" /> {line}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
